import React from 'react';
import { Upload, Loader2, FileSpreadsheet } from 'lucide-react';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';

export const BulkUploadModal = ({
  open,
  onOpenChange,
  uploadFileRef,
  uploading,
  uploadResult,
  onDownloadTemplate,
  onUploadFile,
}) => {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg bg-[#050810] border-[#1e2a45]">
        <DialogHeader>
          <DialogTitle className="text-xl font-bold text-white flex items-center gap-2">
            <Upload className="h-5 w-5 text-[#8B5CF6]" />
            Bulk Upload Ideas
          </DialogTitle>
          <DialogDescription className="text-gray-400">
            Upload multiple ideas at once using CSV or Excel files. All ideas go to pending status for admin review.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 mt-4">
          {/* Download Template */}
          <div className="bg-[#1e2a45]/50 rounded-xl p-4">
            <h4 className="text-white font-medium mb-2 flex items-center gap-2">
              <FileSpreadsheet className="h-4 w-4 text-[#3B82F6]" />
              Step 1: Download Template
            </h4>
            <p className="text-xs text-gray-400 mb-3">Use our template to ensure your data is formatted correctly.</p>
            <div className="flex gap-2">
              <Button
                onClick={() => onDownloadTemplate('csv')}
                size="sm"
                className="bg-[#3B82F6] hover:bg-[#2563EB] text-white text-xs"
              >
                CSV Template
              </Button>
              <Button
                onClick={() => onDownloadTemplate('xlsx')}
                size="sm"
                className="bg-green-600 hover:bg-green-700 text-white text-xs"
              >
                Excel Template
              </Button>
            </div>
          </div>

          {/* Upload File */}
          <div className="bg-[#1e2a45]/50 rounded-xl p-4">
            <h4 className="text-white font-medium mb-2 flex items-center gap-2">
              <Upload className="h-4 w-4 text-[#8B5CF6]" />
              Step 2: Upload Your File
            </h4>
            <p className="text-xs text-gray-400 mb-3">Required columns: title, description, category, subcategory</p>
            <input
              ref={uploadFileRef}
              type="file"
              accept=".csv,.xlsx"
              onChange={onUploadFile}
              className="hidden"
              id="upload-ideas-file"
            />
            <Button
              onClick={() => uploadFileRef.current?.click()}
              disabled={uploading}
              className="w-full bg-[#8B5CF6] hover:bg-[#7C3AED] text-white"
              data-testid="upload-file-btn"
            >
              {uploading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Upload className="h-4 w-4 mr-2" />}
              {uploading ? 'Uploading...' : 'Choose File'}
            </Button>
          </div>

          {/* Upload Result */}
          {uploadResult && (
            <div className={`rounded-xl p-4 ${uploadResult.errors?.length > 0 && uploadResult.created === 0 ? 'bg-red-500/10 border border-red-500/30' : 'bg-green-500/10 border border-green-500/30'}`}>
              <h4 className={`font-medium mb-2 ${uploadResult.errors?.length > 0 && uploadResult.created === 0 ? 'text-red-400' : 'text-green-400'}`}>
                Upload Result
              </h4>
              <div className="text-sm text-gray-300 space-y-1">
                <p>Processed: {uploadResult.processed || 0} rows</p>
                <p>Created: {uploadResult.created || 0} ideas (pending approval)</p>
                {uploadResult.errors?.length > 0 && (
                  <div className="mt-2">
                    <p className="text-amber-400 font-medium">Errors ({uploadResult.errors.length}):</p>
                    <ul className="text-xs text-red-300 mt-1 space-y-0.5 max-h-32 overflow-y-auto">
                      {uploadResult.errors.slice(0, 5).map((err, idx) => (
                        <li key={`${idx}-${String(err).slice(0, 40)}`}>{err}</li>
                      ))}
                      {uploadResult.errors.length > 5 && (
                        <li className="text-gray-400">...and {uploadResult.errors.length - 5} more errors</li>
                      )}
                    </ul>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Info */}
          <div className="text-xs text-gray-500 border-t border-[#1e2a45] pt-4">
            <p className="font-medium text-gray-400 mb-1">Valid categories:</p>
            <p>world_events, sports, crypto, tech, finance, culture, science, economics</p>
            <p className="mt-2 font-medium text-gray-400">Note:</p>
            <p>All uploaded ideas require admin approval before they appear on the main page.</p>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
